import { useState } from 'react'
import { Download, Eye, FileText, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import {
  isPreviewableImage,
  useSourceFileDownload,
  useSourceFileImageUrl,
} from '@/hooks/use-deal-source-files'
import { FILE_ACTION_CLASSES } from '@/features/deal-review/review-tones'
import type { DealLeadDetail } from '@/types/api'
import { SourceEmail } from '@/components/custom/deal-review/source-email'

function formatFileSize(bytes: number): string {
  const kilobytes = bytes / 1024
  if (kilobytes < 1024) return `${Math.max(1, Math.round(kilobytes))} KB`
  return `${(kilobytes / 1024).toLocaleString('en-US', { maximumFractionDigits: 1 })} MB`
}

// No href: the content route needs the bearer token and issues no signed URL.
export function SourceFilesSection({ lead }: { lead: DealLeadDetail }) {
  const { download, states } = useSourceFileDownload()
  const [previewing, setPreviewing] = useState<SourceFile | null>(null)

  if (lead.sourceFiles.length === 0) {
    return (
      <Typography variant="body-sm" className="italic text-primary/50">
        No files were captured with this message.
      </Typography>
    )
  }

  return (
    <div className="space-y-3">
      {lead.sourceFiles.map((file) => {
        const state = states[file.id] ?? 'idle'
        const name = file.filename ?? SOURCE_FILE_KIND_LABELS[file.kind]
        const Icon = file.kind === 'ORIGINAL_EMAIL' ? Mail : FileText
        const preview = previewKindOf(file)

        return (
          <div
            key={file.id}
            className="flex items-start gap-3 rounded-lg border border-primary/10 bg-white p-3.5"
          >
            <div className="shrink-0 rounded-md bg-primary/5 p-2 text-primary">
              <Icon aria-hidden="true" className="size-3.75" />
            </div>
            <div className="min-w-0 flex-1">
              <Typography
                variant="body"
                className="break-all text-sm font-normal"
              >
                {name}
              </Typography>
              <Typography
                variant="body-sm"
                className="mt-0.75 leading-[1.2] text-primary/70"
              >
                {SOURCE_FILE_KIND_LABELS[file.kind]} ·{' '}
                {formatFileSize(file.byteSize)}
              </Typography>
              {state === 'error' && (
                <Typography
                  variant="body-sm"
                  className="mt-2 leading-[1.4] text-destructive"
                >
                  Could not be downloaded.
                </Typography>
              )}
            </div>
            {preview !== null && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={FILE_ACTION_CLASSES}
                aria-label={`Preview ${name}`}
                onClick={() => setPreviewing(file)}
              >
                <Eye aria-hidden="true" />
                Preview
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={FILE_ACTION_CLASSES}
              disabled={state === 'loading'}
              aria-label={`Download ${name}`}
              onClick={() => void download(file.id, file.filename)}
            >
              <Download aria-hidden="true" />
              {state === 'loading' ? 'Downloading…' : 'Download'}
            </Button>
          </div>
        )
      })}

      <Typography variant="body-sm" className="italic text-primary/70">
        {availabilityNote(lead.sourceFiles[0].availableUntil)}
      </Typography>

      <SourceFilePreviewDialog
        file={previewing}
        onClose={() => setPreviewing(null)}
      />
    </div>
  )
}

type SourceFile = DealLeadDetail['sourceFiles'][number]

// Falls back to the duration when the API omits the date.
function availabilityNote(availableUntil: string | undefined): string {
  const date = new Date(availableUntil ?? '')
  if (Number.isNaN(date.getTime())) return 'Files are kept for 180 days.'
  const formatted = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  return `Available to download until ${formatted}.`
}

// PDFs deliberately excluded (GSCFE-125): a deck needs page navigation, not a lightbox.
function previewKindOf(file: SourceFile): 'email' | 'image' | null {
  if (file.kind === 'ORIGINAL_EMAIL') return 'email'
  if (isPreviewableImage(file.mimeType)) return 'image'
  return null
}

// Radix stacks the layers, so Escape closes this and leaves the sheet open.
export function SourceFilePreviewDialog({
  file,
  onClose,
}: {
  file: SourceFile | null
  onClose: () => void
}) {
  const kind = file ? previewKindOf(file) : null

  return (
    <Dialog open={file !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] w-[92vw] max-w-4xl overflow-y-auto">
        <DialogHeader className="mb-4">
          <DialogTitle className="break-all pr-6">
            {file?.filename ??
              (file ? SOURCE_FILE_KIND_LABELS[file.kind] : 'Preview')}
          </DialogTitle>
        </DialogHeader>

        {/* Keyed by file id so a second attachment never shows the previous one's bytes. */}
        {file !== null && kind === 'email' && (
          <SourceEmail key={file.id} sourceFileId={file.id} />
        )}
        {file !== null && kind === 'image' && (
          <ImagePreview
            key={file.id}
            sourceFileId={file.id}
            mimeType={file.mimeType}
            name={file.filename ?? 'Attached image'}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function ImagePreview({
  sourceFileId,
  mimeType,
  name,
}: {
  sourceFileId: string
  mimeType: string | null | undefined
  name: string
}) {
  const { url, isLoading, isError } = useSourceFileImageUrl(
    sourceFileId,
    mimeType
  )

  if (isLoading) return <Skeleton className="h-96 w-full" />

  if (isError || url === null) {
    return (
      <Typography variant="body-sm" className="leading-5 text-destructive">
        This image could not be loaded. Files are kept for 180 days, so an older
        one may no longer be available.
      </Typography>
    )
  }

  return (
    <img
      src={url}
      alt={name}
      className="mx-auto max-h-[70vh] w-auto max-w-full rounded-md border border-primary/10 bg-white object-contain"
    />
  )
}

const SOURCE_FILE_KIND_LABELS: Record<
  DealLeadDetail['sourceFiles'][number]['kind'],
  string
> = {
  ORIGINAL_EMAIL: 'Original email',
  ATTACHMENT: 'Attachment',
  INLINE_IMAGE: 'Inline image',
}
